import React from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import type { KpiCell } from "../../models/API";

const COLOR_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "None" },
  { value: "orange", label: "Orange" },
  { value: "green", label: "Green" },
  { value: "blue", label: "Blue" },
  { value: "red", label: "Red" },
];

interface KpiStripEditorProps {
  cells: KpiCell[];
  onChange: (cells: KpiCell[]) => void;
  maxCells?: number;
}

/**
 * KpiStripEditor — inline table editor for KPI strip cells.
 * Each row has: label, value, sub (text fields) and color (select).
 * Add/remove/reorder (up/down arrows). Max 6 cells enforced client-side (FR-2.2, FR-2.3).
 */
const KpiStripEditor: React.FC<KpiStripEditorProps> = ({
  cells,
  onChange,
  maxCells = 6,
}) => {
  const handleFieldChange = (
    index: number,
    field: keyof KpiCell,
    value: string
  ) => {
    const updated = cells.map((cell, i) =>
      i === index
        ? { ...cell, [field]: value || (field === "color" ? null : "") }
        : cell
    );
    onChange(updated);
  };

  const handleAdd = () => {
    if (cells.length >= maxCells) return;
    const newCell: KpiCell = {
      __typename: "KpiCell",
      label: "",
      value: "",
      sub: "",
      color: null,
    };
    onChange([...cells, newCell]);
  };

  const handleDelete = (index: number) => {
    onChange(cells.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const updated = [...cells];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onChange(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index >= cells.length - 1) return;
    const updated = [...cells];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onChange(updated);
  };

  return (
    <Box>
      {cells.map((cell, idx) => (
        <Box
          key={idx}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <TextField
            size="small"
            label="Label"
            value={cell.label}
            onChange={(e) => handleFieldChange(idx, "label", e.target.value)}
            sx={{ flex: 2, minWidth: 100 }}
            inputProps={{ maxLength: 50 }}
          />
          <TextField
            size="small"
            label="Value"
            value={cell.value}
            onChange={(e) => handleFieldChange(idx, "value", e.target.value)}
            sx={{ flex: 1, minWidth: 70 }}
            inputProps={{ maxLength: 30 }}
          />
          <TextField
            size="small"
            label="Sub-text"
            value={cell.sub}
            onChange={(e) => handleFieldChange(idx, "sub", e.target.value)}
            sx={{ flex: 2, minWidth: 100 }}
            inputProps={{ maxLength: 100 }}
          />
          <Select
            size="small"
            value={cell.color ?? ""}
            onChange={(e) => handleFieldChange(idx, "color", e.target.value)}
            sx={{ minWidth: 90 }}
            displayEmpty
          >
            {COLOR_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          <Tooltip title="Move up">
            <span>
              <IconButton
                size="small"
                onClick={() => handleMoveUp(idx)}
                disabled={idx === 0}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton
                size="small"
                onClick={() => handleMoveDown(idx)}
                disabled={idx === cells.length - 1}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton size="small" onClick={() => handleDelete(idx)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAdd}
        disabled={cells.length >= maxCells}
        sx={{ mt: 0.5 }}
      >
        Add KPI ({cells.length}/{maxCells})
      </Button>
    </Box>
  );
};

export default KpiStripEditor;
